/**
 * 처방 판정 테스트 패널 — 결과 카드 뷰(설계 결정 11).
 * 요약 줄, 약품별 카드(채택 판정 본문·밀린 룰 접기·강등 줄·약품 수준 줄), 성분 중복 블록,
 * 월령 없음·모르는 룰 종류 안내 줄. 판정 해석은 순수 함수(cardModel·verdict)에 위임하고 조립만.
 */
import React, { useState, ReactNode } from "react";
import Tag from "components/atoms/Tag";
import Button from "components/atoms/Button";
import type {
  DoseReviewData,
  DoseReviewItem,
  DoseFinding,
  DoseReviewRequest,
  RecommendedRangeOut,
} from "api/doseReviewService";
import { ResultErrorBoundary } from "./ResultErrorBoundary";
import { severityLabel, severityTone, reasonPhrase, summaryLineText } from "./verdict";
import {
  cardBadge,
  cardIsPending,
  isUnevaluated,
  sortedApplied,
  supersededFindings,
  effectiveSeverity,
  downgradedItemCount,
  cardTitleName,
  interpretUnitLine,
  supersededLine,
} from "./cardModel";
import {
  Card,
  CardTop,
  CardTitle,
  CardSub,
  BadgeRow,
  CardHeaderRow,
  Chevron,
  CollapsibleBody,
  FindingBlock,
  InfoLine,
  DowngradeLine,
  Citation,
  DupBlock,
  NoticeLine,
  SummaryLine,
  GroupTitle,
  Row,
  WarnText,
} from "./style";

interface Props {
  data: DoseReviewData;
  request: DoseReviewRequest;
  onOpenRule: (ruleId: string) => void;
  /** 줄 슬롯 — 정오 표기 컨트롤·"그때 적용된 룰"을 페이지가 item 카드에 넣는다(D-12·H1). */
  renderItemFooter?: (item: DoseReviewItem) => ReactNode;
  /** 카드 제목 옆 슬롯 — 목록 이름 병기 등(H1). organism은 도메인을 모른 채 자리만 연다. */
  renderItemTitleExtra?: (item: DoseReviewItem) => ReactNode;
  /** 폴백의 [원문 보기] 핸들러. 없으면 버튼을 생략한다(테스트 패널은 넘기지 않는다). */
  onShowRaw?: () => void;
  /**
   * 접이식(상세 화면 개편) — 기본 false면 현행(전부 펼침, 테스트 패널 불변). true면 카드 헤더 클릭으로
   * 토글하고 본문은 CSS 접힘(마운트 유지 → 초안 보존). expandedItems에 든 index만 펼친다.
   */
  collapsible?: boolean;
  expandedItems?: ReadonlySet<number>;
  onToggleItem?: (index: number) => void;
  /** 접힌 카드 헤더 슬롯 — 정오 상태·"작성 중" 등(페이지가 넣는다). collapsible일 때만 그린다. */
  renderItemHeaderExtra?: (item: DoseReviewItem) => ReactNode;
  /** 처방 경고(성분 중복) 블록을 숨긴다 — 상세는 요약에서 보여 중복을 피한다(상세 화면 개편). */
  suppressPrescriptionFindings?: boolean;
}

interface ItemCardCollapse {
  collapsible?: boolean;
  expanded?: boolean;
  onToggle?: (index: number) => void;
  renderItemHeaderExtra?: (item: DoseReviewItem) => ReactNode;
}

const rangeText = (r: RecommendedRangeOut): string => {
  const lo = r.min ?? "—";
  const hi = r.max ?? "—";
  let s = `권장 ${lo}~${hi} ${r.unit}`;
  if (r.dayCap != null) s += ` · 1일 최대 ${r.dayCap}`;
  return s;
};

const segmentText = (f: DoseFinding): string => {
  const s = f.matchedSegment;
  if (!s) return "";
  if (s.isDefault) return "기본 구간";
  const parts: string[] = [];
  if (s.ageMinMonths != null || s.ageMaxMonths != null)
    parts.push(`${s.ageMinMonths ?? ""}~${s.ageMaxMonths ?? ""}개월`);
  if (s.weightMinKg != null || s.weightMaxKg != null)
    parts.push(`${s.weightMinKg ?? ""}~${s.weightMaxKg ?? ""}kg`);
  return parts.length ? `구간 ${parts.join(" · ")}` : "";
};

/** 인용문 — 3줄 넘으면 접히고 더보기로 펼침(AC-52). */
const CitationView: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const collapsible = text.length > 80; // 좁은 패널에서 대략 3줄 초과
  return (
    <div>
      <Citation expanded={expanded || !collapsible} data-testid="dose-citation">
        {text}
      </Citation>
      {collapsible && (
        <Button
          buttonType="button"
          type="Text"
          size="XSmall"
          variant="Ghost"
          color="Secondary"
          onClick={() => setExpanded((v) => !v)}
          data-testid="dose-citation-toggle"
        >
          {expanded ? "접기" : "더보기"}
        </Button>
      )}
    </div>
  );
};

/** 밀린 룰 — 접힌 줄, 펼치면 각 판정 한 줄(AC-46). */
const SupersededFold: React.FC<{ findings: DoseFinding[] }> = ({ findings }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <Button
        buttonType="button"
        type="Text"
        size="XSmall"
        variant="Ghost"
        color="Secondary"
        onClick={() => setOpen((v) => !v)}
        data-testid="dose-superseded-toggle"
      >
        {open ? "▾" : "▸"} 밀린 룰 {findings.length}개
      </Button>
      {open &&
        findings.map((f, i) => (
          <InfoLine key={i} data-testid="dose-superseded-line">
            {supersededLine(f, severityLabel(effectiveSeverity(f), f.status))}
          </InfoLine>
        ))}
    </div>
  );
};

const ItemLevelLines: React.FC<{ item: DoseReviewItem; dose?: number }> = ({ item, dose }) => {
  const unit = interpretUnitLine(dose, item.resolved?.prescribeUnit);
  return (
    <>
      {unit && <InfoLine data-testid="dose-unit-line">{unit}</InfoLine>}
      {item.codeConflict === true && (
        <WarnText data-testid="dose-code-conflict">
          표준코드와 보험코드가 서로 다른 약을 가리킵니다
        </WarnText>
      )}
      {(item.labelNotes ?? []).map((n, i) => (
        <InfoLine key={i} data-testid="dose-label-note">
          {n.message}
        </InfoLine>
      ))}
    </>
  );
};

/** 카드 껍데기 — collapsible이면 헤더 클릭 토글 + 본문 CSS 접힘(마운트 유지), 아니면 현행. */
const CardShell: React.FC<{
  index: number;
  muted?: boolean;
  uneval?: boolean;
  header: ReactNode;
  body: ReactNode;
  collapse: ItemCardCollapse;
}> = ({ index, muted, uneval, header, body, collapse }) => {
  const { collapsible, expanded = true, onToggle } = collapse;
  const cardProps = {
    "data-testid": "dose-card",
    "data-dose-item": index,
    ...(muted ? { muted: true } : {}),
    ...(uneval ? { "data-uneval": "true" } : {}),
  } as Record<string, unknown>;
  if (!collapsible) {
    return (
      <Card {...cardProps}>
        <CardTop>{header}</CardTop>
        {body}
      </Card>
    );
  }
  const toggle = () => onToggle?.(index);
  return (
    <Card {...cardProps}>
      <CardHeaderRow
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        data-testid="dose-card-header"
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <CardTop style={{ flex: 1 }}>{header}</CardTop>
        <Chevron>{expanded ? "▾" : "▸"}</Chevron>
      </CardHeaderRow>
      <CollapsibleBody $open={expanded}>{body}</CollapsibleBody>
    </Card>
  );
};

const ItemCard: React.FC<{
  item: DoseReviewItem;
  request: DoseReviewRequest;
  onOpenRule: (ruleId: string) => void;
  renderItemFooter?: (item: DoseReviewItem) => ReactNode;
  renderItemTitleExtra?: (item: DoseReviewItem) => ReactNode;
  collapse: ItemCardCollapse;
}> = ({ item, request, onOpenRule, renderItemFooter, renderItemTitleExtra, collapse }) => {
  const p = request.prescriptions[item.index] ?? {};
  const title = cardTitleName(request, item.index) || `약품 ${item.index + 1}`;
  const dose = p.dosagePerAdministration;
  const freq = p.timesPerDay;
  const sub = `1회 ${dose ?? "—"} × ${freq ?? "—"}회`;
  const headerExtra = collapse.collapsible ? collapse.renderItemHeaderExtra?.(item) : null;

  if (isUnevaluated(item)) {
    const header = (
      <>
        <div>
          <CardTitle>{title}</CardTitle>
          {renderItemTitleExtra?.(item)}
          <CardSub>{sub}</CardSub>
        </div>
        {headerExtra && <BadgeRow>{headerExtra}</BadgeRow>}
      </>
    );
    const body = (
      <>
        <InfoLine data-testid="dose-uneval-reason">
          {reasonPhrase(item.unevaluatedReason)}
          {item.unevaluatedReason === "not_eligible" && item.ineligibleReason
            ? ` (${item.ineligibleReason})`
            : ""}
        </InfoLine>
        <ItemLevelLines item={item} dose={dose} />
        {renderItemFooter?.(item)}
      </>
    );
    return <CardShell index={item.index} muted uneval header={header} body={body} collapse={collapse} />;
  }

  const badge = cardBadge(item);
  const pending = cardIsPending(item);
  const applied = sortedApplied(item);
  const superseded = supersededFindings(item);

  const header = (
    <>
      <div>
        <CardTitle>{title}</CardTitle>
        {renderItemTitleExtra?.(item)}
        <CardSub>{sub}</CardSub>
      </div>
      <BadgeRow>
        {badge && (
          <Tag
            size="Small"
            variant={badge.downgraded ? "Outlined" : "Tinted"}
            color={severityTone(badge.severity, badge.status)}
          >
            {severityLabel(badge.severity, badge.status)}
          </Tag>
        )}
        {pending && (
          <Tag size="Small" variant="Tinted" color="Gray">
            검수 대기
          </Tag>
        )}
        {headerExtra}
      </BadgeRow>
    </>
  );

  const body = (
    <>
      {applied.map((f, idx) => (
        <FindingBlock key={idx}>
          <InfoLine>{f.reason}</InfoLine>
          {f.recommendedRange && <InfoLine>{rangeText(f.recommendedRange)}</InfoLine>}
          <Row>
            {f.scope !== "pharmacy" && f.ruleId && (
              <Button
                buttonType="button"
                type="Text"
                size="XSmall"
                variant="Ghost"
                color="Primary"
                onClick={() => onOpenRule(f.ruleId)}
                data-testid="dose-rule-link"
              >
                #{f.ruleId}
              </Button>
            )}
            {segmentText(f) && <CardSub>{segmentText(f)}</CardSub>}
            {/* 판정 줄 표식은 채택 판정이 여럿일 때만 — 1건이면 카드 표식으로 충분(설계 결정 11-2). */}
            {applied.length > 1 && f.pendingReview && (
              <Tag size="Small" variant="Tinted" color="Gray">
                검수 대기
              </Tag>
            )}
          </Row>
          {f.pendingDowngrade && (
            <DowngradeLine data-testid="dose-downgrade-line">
              지금은 {severityLabel(f.pendingDowngrade.to)}으로 나감 · 승인하면{" "}
              {severityLabel(f.pendingDowngrade.from)}으로 나갈 수 있음
            </DowngradeLine>
          )}
          {f.provenance?.citation && <CitationView text={f.provenance.citation} />}
        </FindingBlock>
      ))}

      {superseded.length > 0 && <SupersededFold findings={superseded} />}
      <ItemLevelLines item={item} dose={dose} />
      {renderItemFooter?.(item)}
    </>
  );

  return <CardShell index={item.index} header={header} body={body} collapse={collapse} />;
};

const ResultViewBody: React.FC<Props> = ({
  data,
  request,
  onOpenRule,
  renderItemFooter,
  renderItemTitleExtra,
  onShowRaw,
  collapsible,
  expandedItems,
  onToggleItem,
  renderItemHeaderExtra,
  suppressPrescriptionFindings,
}) => {
  return (
    <div>
        {data.patient?.ageMonths === null && (
          <NoticeLine data-testid="dose-notice-age">
            월령 없음 — 연령 구간은 체중으로만 맞춰졌거나 맞춰지지 않았습니다
          </NoticeLine>
        )}
        {data.unknownRuleClasses && data.unknownRuleClasses.length > 0 && (
          <NoticeLine data-testid="dose-notice-unknown">
            판정 엔진이 모르는 룰 종류가 있어 판정에서 빠졌습니다: {data.unknownRuleClasses.join(", ")}
          </NoticeLine>
        )}

        <SummaryLine data-testid="dose-summary">
          {summaryLineText(data.summary, downgradedItemCount(data))}
        </SummaryLine>

        {data.items.map((item) => (
          <ResultErrorBoundary key={item.index} onShowRaw={onShowRaw}>
            <ItemCard
              item={item}
              request={request}
              onOpenRule={onOpenRule}
              renderItemFooter={renderItemFooter}
              renderItemTitleExtra={renderItemTitleExtra}
              collapse={{
                collapsible,
                expanded: collapsible ? !!expandedItems?.has(item.index) : true,
                onToggle: onToggleItem,
                renderItemHeaderExtra,
              }}
            />
          </ResultErrorBoundary>
        ))}

        {!suppressPrescriptionFindings && data.prescriptionFindings.length > 0 && (
          <>
            <GroupTitle data-testid="dose-prescription-warnings-title">처방 경고</GroupTitle>
            {data.prescriptionFindings.map((pf, i) => (
              <DupBlock key={i} data-testid="dose-dup">
                <BadgeRow>
                  <Tag size="Small" variant="Tinted" color={severityTone(pf.severity)}>
                    {severityLabel(pf.severity)}
                  </Tag>
                  <strong>성분 중복</strong>
                </BadgeRow>
                <InfoLine>{pf.message}</InfoLine>
              </DupBlock>
            ))}
          </>
        )}
    </div>
  );
};

/**
 * 결과 카드 organism. 최상위 ErrorBoundary로 ResultViewBody를 감싼다 — 경계는 반드시
 * 던지는 쪽의 부모여야 하므로(자기 반환 트리 안의 경계는 자기 render throw를 못 잡는다)
 * body를 별도 컴포넌트로 분리해 바깥에서 감싼다. 카드 단위 경계는 body 안에 있다.
 */
const ResultView: React.FC<Props> = (props) => (
  <ResultErrorBoundary onShowRaw={props.onShowRaw}>
    <ResultViewBody {...props} />
  </ResultErrorBoundary>
);

export default ResultView;
